import { ArrowDown } from 'lucide-react';
import { getPublishedArticles } from '@/data/insights';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';
import { scrollToId } from '@/lib/scroll';

// Two-column hero. Right side is a restrained editorial collage built ONLY
// from approved article imagery already in the project — no stock graphics,
// no fake covers, no SEO clipart.
export const InsightsHero = () => {
  useLiveTick();
  const articles = getPublishedArticles();
  const [lead, second, third] = articles;

  return (
    <section aria-labelledby="insights-hero-heading" data-testid="insights-hero" className="bg-white py-16 sm:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <Reveal>
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">01</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
              Insights &amp; Updates
            </span>
          </p>
          <h1
            id="insights-hero-heading"
            data-testid="insights-hero-heading"
            className="mt-4 text-balance text-4xl font-extrabold tracking-tight text-navy-900 sm:text-5xl"
          >
            Practical insights for building a stronger business
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Explore practical advice, ideas and resources from Amden Group across operations,
            people, growth and business launch.
          </p>
          <p className="mt-3 max-w-xl border-l-2 border-gold-500 pl-4 text-sm font-medium italic leading-relaxed text-navy-800">
            Clear thinking, practical guidance and lessons drawn from real business challenges.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => scrollToId('#latest-insights')}
              data-testid="insights-explore-latest-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
            >
              Explore Latest Insights
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => scrollToId('#explore-topics')}
              data-testid="insights-browse-topics-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy-900 px-6 py-3 text-sm font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
            >
              Browse by Topic
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
            </button>
          </div>
        </Reveal>

        {lead && (
          <Reveal delay={0.12} className="relative">
            <div className="relative">
              <img
                src={lead.image?.src}
                alt={lead.image?.alt || lead.title}
                width={lead.image.width}
                height={lead.image.height}
                data-testid="insights-hero-image"
                className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lift"
              />
              <div className="absolute -bottom-8 -left-4 hidden w-44 sm:block lg:-left-10 lg:w-52">
                {second && (
                  <img
                    src={second.image.src}
                    alt={second.image.alt}
                    width={second.image.width}
                    height={second.image.height}
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-xl border-4 border-white object-cover shadow-card"
                  />
                )}
              </div>
              <div className="absolute -right-4 -top-8 hidden w-40 sm:block lg:-right-8 lg:w-48">
                {third && (
                  <img
                    src={third.image.src}
                    alt={third.image.alt}
                    width={third.image.width}
                    height={third.image.height}
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-xl border-4 border-white object-cover shadow-card"
                  />
                )}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
